import React from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Col, Popover, Row, Space, Tag, Typography } from "antd";
import { DATE_FORMAT } from "config/constants";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { formatDateTime } from "utils/dateTime";
import { isNil, findIndex, uniqBy, map, compact, isArray } from "lodash";
import { formatCurrency } from "utils/helperFuncs";

const COMBO_CAMPAIGN = {
  DISCOUNT_MONEY: "DISCOUNT_MONEY",
  GIFT: "GIFT",
  FIXED_PRICE: "FIXED_PRICE",
};

const DISCOUNT_TYPE = {
  DISCOUNT_MONEY: "DISCOUNT_MONEY",
  DISCOUNT_PERCENTAGE: "DISCOUNT_PERCENTAGE",
};

const PROMOTION_CAMPAIGN_STATUS = {
  UPCOMING: "UPCOMING",
  ON_GOING: "ON_GOING",
};

const filterPromotionCampaignTierByType = (conditionProductGroups, type) => {
  if (isArray(conditionProductGroups)) {
    return conditionProductGroups.filter((obj) => obj.promotionCampaignTier.type === type);
  }
  return [];
};

const convertConditionPromotionProductsOnGoing = (conditionProductGroups, typePromotion) => {
  const promotionCampaign = uniqBy(conditionProductGroups, (obj) => obj.promotionCampaign.id).map(
    (obj) => obj.promotionCampaign
  );

  const promotionCampaignTiers = promotionCampaign.map((obj) => {
    const getPromotionCampaignTiers = conditionProductGroups
      .filter((element) => {
        return element.promotionCampaign.id === obj.id;
      })
      .map((element) => element.promotionCampaignTier);

    return {
      typePromotion,
      promotionCampaign: obj,
      promotionCampaignTiers: getPromotionCampaignTiers,
    };
  });

  return promotionCampaignTiers;
};

const arrangeConditionProductGroups = (conditionProductGroups = [], codeFirstItem) => {
  const getIndexConditionProductGroupByCodeFirstItem = findIndex(
    conditionProductGroups,
    function (conditionProductGroup, index) {
      return conditionProductGroup.conditionPromotionProducts.find(
        (conditionPromotionProducts) => conditionPromotionProducts.product.code === codeFirstItem
      );
    }
  );

  return [
    ...conditionProductGroups.slice(-getIndexConditionProductGroupByCodeFirstItem),
    ...conditionProductGroups.slice(0, -getIndexConditionProductGroupByCodeFirstItem),
  ]
    .map((product, index) => {
      if (index === 0) {
        return `[${product.quantity} ${codeFirstItem}]`;
      }
      return `[${product.quantity} ${product.conditionPromotionProducts[0].product.code}]`;
    })
    .join(" + ");
};

const renderDiscountType = (discountAmount) => {
  return {
    [`${DISCOUNT_TYPE.DISCOUNT_MONEY}`]: formatCurrency(discountAmount),
    [`${DISCOUNT_TYPE.DISCOUNT_PERCENTAGE}`]: `${discountAmount} %`,
  };
};

const LOYALTY_AWARD_UNIT = {
  MONEY_PER_ITEM: "MONEY_PER_ITEM",
  MONEY_PER_AWARD: "MONEY_PER_AWARD",
  LOYALTY_POINT: "LOYALTY_POINT",
  GIFT_PER_AWARD: "GIFT_PER_AWARD",
};

// 1 HAP = 1.000đ, BE lưu awardValue ở dạng VNĐ
const HAP_TO_VND = 1000;

const filterByCampaignType = (promotions, campaignType) =>
  isArray(promotions) ? promotions.filter((obj) => obj?.promotionCampaign?.campaignType === campaignType) : [];

const renderAwardText = ({ award }) => {
  if (award?.awardAmountTitle) return award.awardAmountTitle;
  switch (award?.awardUnit) {
    case LOYALTY_AWARD_UNIT.MONEY_PER_ITEM:
      return `${formatCurrency(award.awardValue)}/sp`;
    case LOYALTY_AWARD_UNIT.MONEY_PER_AWARD:
      return `${formatCurrency(award.awardValue)}/mốc`;
    case LOYALTY_AWARD_UNIT.LOYALTY_POINT:
      return `${award.awardValue / HAP_TO_VND} HAP`;
    default:
      return "Quà tặng";
  }
};

const sortAwards = (awards = []) =>
  [...awards].sort((a, b) => (a?.award?.position ?? a?.minItems ?? 0) - (b?.award?.position ?? b?.minItems ?? 0));

const ROUNDED_BLUE_TAG_STYLE = {
  color: "#2246DC",
  backgroundColor: "#E7F0FF",
  borderColor: "#2246DC",
  borderRadius: 16,
};

const PromotionTag = ({ children, fit = false }) => (
  <Tag
    color="#E7F0FF"
    style={{
      color: "#000",
      borderColor: "#D8DFEA",
      width: fit ? "fit-content" : "100%",
      display: fit ? "inline-flex" : "flex",
      justifyContent: "center",
      margin: 0,
      ...ROUNDED_BLUE_TAG_STYLE,
    }}
  >
    <Space align="center" style={{ height: "24px" }}>
      <Typography.Text style={{ color: ROUNDED_BLUE_TAG_STYLE.color }}>{children}</Typography.Text>
      <InfoCircleOutlined />
    </Space>
  </Tag>
);

const ProductPromotions = ({ promotions, fit }) => {
  const { t } = useTranslation();
  return promotions.map(({ promotionCampaign, promotionCampaignTier: tier }) => (
    <Popover
      key={`${promotionCampaign?.id}-${tier?.id}`}
      trigger={["click"]}
      content={
        <Row style={{ width: 400 }}>
          <Col span={12}>
            <Typography.Text strong>{t("product.belongsToTheProgram")}</Typography.Text>
          </Col>
          <Col span={12}>
            <Link
              to={`/campaign/detail/${promotionCampaign.id}?campaignType=${promotionCampaign.campaignType}`}
              target="_blank"
            >
              <Link
                to={`/campaign/detail/${promotionCampaign.id}?campaignType=${promotionCampaign.campaignType}`}
                target="_blank"
              >
                <Typography.Link>{promotionCampaign.name}</Typography.Link>
              </Link>
            </Link>
          </Col>
          <Col span={12}>
            <Typography.Text strong>{t("product.programTime")}</Typography.Text>
          </Col>
          <Col span={12}>
            <Space size={0} direction="vertical">
              <Typography.Text>
                {t("product.from")}: {formatDateTime(promotionCampaign?.startDate, DATE_FORMAT)}
              </Typography.Text>
              <Typography.Text>
                {t("product.to")}: {formatDateTime(promotionCampaign?.endDate, DATE_FORMAT)}
              </Typography.Text>
            </Space>
          </Col>
          <Col span={24}>
            <Typography.Paragraph style={{ marginTop: 8 }}>
              {t("product.buy")}{" "}
              <Typography.Text strong>
                {tier?.minQuantity ? `từ [${tier.minQuantity}] sản phẩm` : "bất kỳ số lượng"}
              </Typography.Text>
              , {t("product.reduce")}{" "}
              <Typography.Text strong>
                [{renderDiscountType(tier?.discountAmount)[tier?.discountType] || "--"}]/1 sản phẩm
              </Typography.Text>
              {tier?.maxQuantityPerSeller ? ` (tối đa ${tier.maxQuantityPerSeller} sản phẩm/đại lý)` : ""}
            </Typography.Paragraph>
          </Col>
        </Row>
      }
    >
      <span>
        <PromotionTag fit={fit}>Khuyến mãi trên 1 sản phẩm</PromotionTag>
      </span>
    </Popover>
  ));
};

const LoyaltyPackages = ({ periods, fit }) => {
  const { t } = useTranslation();
  return periods.map((period) => (
    <Popover
      key={period.id}
      trigger={["click"]}
      content={
        <Row style={{ width: 400 }}>
          <Col span={12}>
            <Typography.Text strong>Gói chiết khấu</Typography.Text>
          </Col>
          <Col span={12}>
            <Link to={`/loyalty-package/package/${period.id}`} target="_blank">
              <Typography.Link>{period.title || "--"}</Typography.Link>
            </Link>
          </Col>
          <Col span={12}>
            <Typography.Text strong>{t("product.programTime")}</Typography.Text>
          </Col>
          <Col span={12}>
            <Space size={0} direction="vertical">
              <Typography.Text>
                {t("product.from")}: {formatDateTime(period.startTime, DATE_FORMAT)}
              </Typography.Text>
              <Typography.Text>
                {t("product.to")}: {formatDateTime(period.endTime, DATE_FORMAT)}
              </Typography.Text>
            </Space>
          </Col>
          <Col span={24} style={{ marginTop: 8 }}>
            {sortAwards(period.productConfig?.awards).map((item, index) => (
              <Typography.Paragraph key={index} style={{ marginBottom: 4 }}>
                Mua từ <Typography.Text strong>[{item.minItems ?? 0}]</Typography.Text>
                {item.maxItems ? (
                  <>
                    {" "}
                    đến <Typography.Text strong>[{item.maxItems}]</Typography.Text>
                  </>
                ) : null}{" "}
                sản phẩm: <Typography.Text strong>{renderAwardText(item)}</Typography.Text>
              </Typography.Paragraph>
            ))}
          </Col>
        </Row>
      }
    >
      <span>
        <PromotionTag fit={fit}>{period.title || "Gói chiết khấu"}</PromotionTag>
      </span>
    </Popover>
  ));
};

// Chính sách giá "mua càng nhiều, giảm càng sâu" (productDiscounts), cùng nguồn với tag "Giá tốt nhất khi mua từ x sp" trên mobile
const QuantityDiscounts = ({ discounts, price, fit }) => (
  <Popover
    trigger={["click"]}
    content={
      <div style={{ width: 360 }}>
        <Typography.Text strong>Mua càng nhiều, giảm càng sâu</Typography.Text>
        {discounts.map((item, index) => (
          <Typography.Paragraph key={index} style={{ margin: "4px 0 0" }}>
            Mua từ <Typography.Text strong>[{item.minQuantity}]</Typography.Text>
            {item.maxQuantity ? (
              <>
                {" "}
                đến <Typography.Text strong>[{item.maxQuantity}]</Typography.Text>
              </>
            ) : null}{" "}
            sản phẩm, giảm <Typography.Text strong>[{formatCurrency(item.discountAmount)}]/sp</Typography.Text>
            {!isNil(price) ? (
              <>
                , giá về <Typography.Text strong>{formatCurrency(price - item.discountAmount)}/sp</Typography.Text>
              </>
            ) : null}
          </Typography.Paragraph>
        ))}
      </div>
    }
  >
    <span>
      <PromotionTag fit={fit}>Mua càng nhiều, giảm càng sâu</PromotionTag>
    </span>
  </Popover>
);

const PopoverPromotion = ({
  conditionPromotionProductsOnGoing: allPromotionsOnGoing,
  belongLoyaltyPackagePeriods,
  productDiscounts,
  price,
  productCode,
  roundedTags = false,
}) => {
  const { t } = useTranslation();
  // API trả cả CTKM 1 sản phẩm lẫn combo: combo hiển thị theo popover riêng bên dưới
  const conditionPromotionProductsOnGoing = isArray(allPromotionsOnGoing)
    ? filterByCampaignType(allPromotionsOnGoing, "COMBO_PROMO")
    : allPromotionsOnGoing;
  const productPromotions = filterByCampaignType(allPromotionsOnGoing, "PRODUCT_PROMO");
  const loyaltyPeriods = isArray(belongLoyaltyPackagePeriods) ? belongLoyaltyPackagePeriods : [];
  const quantityDiscounts = isArray(productDiscounts)
    ? productDiscounts.filter((item) => item?.discountAmount > 0)
    : [];
  const renderTypePromotions = {
    [`${COMBO_CAMPAIGN.DISCOUNT_MONEY}`]: t("campaign.combo.comboDiscount"),
    [`${COMBO_CAMPAIGN.GIFT}`]: t("campaign.combo.comboGift"),
    [`${COMBO_CAMPAIGN.FIXED_PRICE}`]: t("campaign.combo.comboFixedPrice"),
  };
  const dataTagPromotion = compact(
    map(
      Object.values(COMBO_CAMPAIGN).map((item) =>
        convertConditionPromotionProductsOnGoing(
          filterPromotionCampaignTierByType(conditionPromotionProductsOnGoing, item),
          item
        )
      ),
      0
    )
  );

  const renderPromotionCampaignTierType = {
    [`${PROMOTION_CAMPAIGN_STATUS.UPCOMING}`]: {
      background: "#FFF4EC",
      color: "#EF9351",
      label: t("campaign.upcoming"),
    },
    [`${PROMOTION_CAMPAIGN_STATUS.ON_GOING}`]: {
      background: "#EDF9F0",

      color: "#32BF46",
      label: t("campaign.onGoing"),
    },
  };

  const renderCombos = (promotionCampaignTiers, typePromotion) =>
    // eslint-disable-next-line array-callback-return
    promotionCampaignTiers.map((item, index) => {
      switch (typePromotion) {
        case COMBO_CAMPAIGN.DISCOUNT_MONEY:
          return (
            <Typography.Paragraph>
              <Typography.Text strong>
                {t("campaign.combo.comboDiscount")} {index + 1}:{" "}
              </Typography.Text>
              {t("product.buy")}{" "}
              <Typography.Text strong>
                {arrangeConditionProductGroups(item.conditionProductGroups, productCode)}
              </Typography.Text>{" "}
              {t("product.minimum")}
              <Typography.Text strong> [{item.minQuantity}] </Typography.Text>
              {t("product.combo")}, {t("product.reduce")}{" "}
              <Typography.Text strong>
                [{renderDiscountType(item.discountAmount)[item.discountType]}]/1{" "}
              </Typography.Text>
              {t("product.combo")}
            </Typography.Paragraph>
          );
        case COMBO_CAMPAIGN.GIFT:
          break;
        case COMBO_CAMPAIGN.FIXED_PRICE:
          return (
            <Typography.Paragraph>
              <Typography.Text strong>
                {t("campaign.combo.comboFixedPrice")} {index + 1}:{" "}
              </Typography.Text>
              {t("product.buy")}{" "}
              <Typography.Text strong>
                {arrangeConditionProductGroups(item.conditionProductGroups, productCode)}
              </Typography.Text>{" "}
              {t("product.justForThePrice")}
              <Typography.Text strong> [{formatCurrency(item.fixedComboPrice)}] </Typography.Text>
            </Typography.Paragraph>
          );
      }
    });

  return (
    (!isNil(conditionPromotionProductsOnGoing) || loyaltyPeriods.length > 0 || quantityDiscounts.length > 0) && (
      <Space direction="vertical" align={roundedTags ? "center" : undefined} style={{ width: "100%" }}>
        <ProductPromotions promotions={productPromotions} fit={roundedTags} />
        {dataTagPromotion.map(({ typePromotion, promotionCampaign, promotionCampaignTiers }) => (
          <Popover
            trigger={["click"]}
            content={
              <Row style={{ width: 500 }}>
                <Col span={12}>
                  <Typography.Text strong>{t("product.belongsToTheProgram")}</Typography.Text>
                </Col>
                <Col span={12}>
                  <Link
                    to={`/campaign/detail/${promotionCampaign.id}?campaignType=${promotionCampaign.campaignType}`}
                    target="_blank"
                  >
                    <Typography.Link>{promotionCampaign.name}</Typography.Link>
                  </Link>
                </Col>
                <Col span={12}>
                  <Typography.Text strong>{t("product.programTime")}</Typography.Text>
                </Col>
                <Col span={12}>
                  <Space size={0} direction="vertical">
                    <Typography.Text>
                      {t("product.from")}: {formatDateTime(promotionCampaign?.startDate, DATE_FORMAT)}
                    </Typography.Text>
                    <Typography.Text>
                      {t("product.to")}: {formatDateTime(promotionCampaign?.endDate, DATE_FORMAT)}
                    </Typography.Text>
                  </Space>
                </Col>
                <Col span={24}>
                  <Typography>
                    {renderCombos(promotionCampaignTiers, typePromotion)}

                    <Typography.Paragraph>
                      {t("product.cannotBeAppliedConcurrentlyWithProgramType")}
                    </Typography.Paragraph>
                  </Typography>
                </Col>
              </Row>
            }
            title={
              <Space style={{ width: "100%", justifyContent: "end" }}>
                <Tag color={renderPromotionCampaignTierType[`${promotionCampaign?.status}`].background}>
                  <Typography.Text
                    style={{ color: renderPromotionCampaignTierType[`${promotionCampaign?.status}`].color }}
                  >
                    {renderPromotionCampaignTierType[`${promotionCampaign?.status}`].label}
                  </Typography.Text>
                </Tag>
              </Space>
            }
          >
            <span>
              <PromotionTag fit={roundedTags}>{renderTypePromotions[typePromotion]}</PromotionTag>
            </span>
          </Popover>
        ))}
        {quantityDiscounts.length > 0 && (
          <QuantityDiscounts discounts={quantityDiscounts} price={price} fit={roundedTags} />
        )}
        <LoyaltyPackages periods={loyaltyPeriods} fit={roundedTags} />
      </Space>
    )
  );
};

export default PopoverPromotion;
